import { ElasticCourseData, ElasticRoundAnalytics, ElasticRoundAnalyticsRound, StrokeDraftDataPlayerStatusEnum, TournamentEventTypeEnum } from '@spikemark/rest-api';
import last from 'lodash/last';
import range from 'lodash/range';
import sum from 'lodash/sum';
import { FC, memo, PropsWithChildren, ReactNode, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { displayHoleResult, displayScoreResult, displayTeamName } from '../../utils/format';
import { MascotWithName } from '../mascot-with-name';
import { PlayerLink, TeamLink } from '../navigation/entity-links';
import { ScoreToPar } from '../score-to-par';
import { OverallRankDisplay } from '../table/leaderboard/columns/shared';
import { Button } from '../ui/button';
import { Dialog, DialogClose, DialogContent } from '../ui/dialog';
export type ScorecardDialogProps = {
  data: ElasticRoundAnalytics;
  format: TournamentEventTypeEnum;
  playerRankingsDisplayCutoff?: number;
  courseInfoMap: Record<string, ElasticCourseData>;
  isOpen: boolean;
  suppressProfileLinks?: boolean;
  onClose: () => void;
};
export const ScorecardDialog: FC<ScorecardDialogProps> = memo(({
  data,
  format,
  playerRankingsDisplayCutoff,
  courseInfoMap,
  isOpen,
  suppressProfileLinks,
  onClose
}) => {
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open) {
      onClose();
    }
  }}>
        <DialogContent className="w-full flex flex-col lg:max-w-5xl xl:max-w-7xl lg:max-h-[90vh]" compact>
          <DialogClose />
          <header className="mb-4 flex flex-col lg:flex-row lg:items-center justify-between m-4 lg:mb-0">
            <div className="flex items-center">
              <MascotWithName alt={data.playerName!} variant="medium" path={data.userPicture} displayMode="player">
                <div className="flex items-center">
                  <h2 className="text-xl lg:text-2xl font-bold mb-1 mr-2 lg:mr-4">
                    {data.playerName}
                  </h2>
                  <div className="hidden lg:block">
                    <OverallRankDisplay displayCutoff={playerRankingsDisplayCutoff} overallRank={data.overallPlayerRank} />
                  </div>
                </div>
                <MascotWithName variant="small" displayMode="team" alt={data.schoolName} path={data.schoolLogo}>
                  <p className="text-sm">
                    {displayTeamName(data.schoolName, data.teamLabel, data.playerId)}
                  </p>
                </MascotWithName>
              </MascotWithName>
              <div className="ml-4 lg:ml-8 flex flex-col items-center lg:border-l lg:border-l-gray-300 lg:pl-3 space-y-1 -mt-3 lg:mt-0">
                <ScoreToPar size="large" score={data.totalScore} square />
              </div>
            </div>
            {!suppressProfileLinks && <div className="flex items-center space-x-2 lg:space-x-4 self-center lg:self-end mt-4 lg:mt-0">
                <Button variant="outline" asChild>
                  <PlayerLink playerId={data.playerId}>
                    <span className="hidden lg:inline mr-1">View</span>Player Profile
                  </PlayerLink>
                </Button>
                <Button variant="outline" asChild>
                  <TeamLink schoolId={data.schoolId}>
                    <span className="hidden lg:inline mr-1">View</span>Team Profile
                  </TeamLink>
                </Button>
              </div>}
          </header>
          {courseInfoMap ? <div className="space-y-4 flex-1 overflow-auto lg:m-4">
              {data.rounds?.map((round, i) => <RoundScorecard key={round.roundId} data={round} courseInfo={courseInfoMap[`${round.roundId}-${data.courseLabels?.[i] ?? 'A'}`]} totalStrokes={data.strokes[i]} totalScore={data.scores[i]} format={format} />) ?? <p className="p-4">This player has not started any rounds yet.</p>}
            </div> : null}
        </DialogContent>
      </Dialog>;
});
const Cell: FC<PropsWithChildren<{
  cellType?: 'cell' | 'header';
  variant?: 'total';
  isStartingHole?: boolean;
}>> = memo(({
  children,
  cellType = 'cell',
  variant,
  isStartingHole
}) => {
  const Tag = cellType === 'header' ? 'th' : 'td';
  return <Tag className={cn('p-[3px] lg:p-2 border-gray-200 first:pl-4 first:text-left text-center', {
    'bg-gray-50 border-t border-b': cellType === 'header',
    'border-l border-r': variant === 'total',
    'bg-amber-300/30': isStartingHole
  })}>
      {children}
    </Tag>;
});
const RoundScorecardRow: FC<{
  label: string;
  cellType?: 'cell' | 'header';
  data: (number | null)[];
  total?: number;
  accumulator: 'sum' | 'last';
  startingHole?: number;
  cellRenderer?: (value: number | null, index: number) => ReactNode;
  totalCellRenderer?: (value: number | null, start?: number, end?: number) => ReactNode;
}> = ({
  label,
  cellType = 'cell',
  data,
  total,
  accumulator,
  startingHole = 1,
  cellRenderer = (value: number | null) => value,
  totalCellRenderer = (value: number | null) => value
}) => {
  const accum = accumulator === 'last' ? last : sum;
  return <tr data-sentry-component="RoundScorecardRow" data-sentry-source-file="scorecard-dialog.tsx">
      <Cell cellType={cellType} data-sentry-element="Cell" data-sentry-source-file="scorecard-dialog.tsx">{label}</Cell>
      {range(1, 10).map(holeId => <Cell cellType={cellType} key={holeId} isStartingHole={holeId === startingHole}>
          {cellRenderer(data[holeId - 1], holeId - 1)}
        </Cell>)}
      <Cell cellType={cellType} variant="total" data-sentry-element="Cell" data-sentry-source-file="scorecard-dialog.tsx">
        {data.slice(0, 9).every(x => typeof x === 'number') ? totalCellRenderer(accum(data.slice(0, 9)) as number | null, 0, 9) : '-'}
      </Cell>
      {range(10, 19).map(holeId => <Cell cellType={cellType} key={holeId} isStartingHole={holeId === startingHole}>
          {cellRenderer(data[holeId - 1], holeId - 1)}
        </Cell>)}
      <Cell cellType={cellType} variant="total" data-sentry-element="Cell" data-sentry-source-file="scorecard-dialog.tsx">
        {data.slice(9, 18).every(x => typeof x === 'number') ? totalCellRenderer(accum(data.slice(9, 18)) as number | null, 9, 18) : '-'}
      </Cell>
      <Cell cellType={cellType} variant="total" data-sentry-element="Cell" data-sentry-source-file="scorecard-dialog.tsx">
        {data.every(x => typeof x === 'number') ? totalCellRenderer(total ?? accum(data) as number) : '-'}
      </Cell>
    </tr>;
};
const normaliseHoles = (values: (number | null)[], startingHole: number = 1) => values.slice(startingHole - 1).concat(values.slice(0, startingHole - 1));
const RoundScorecard: FC<{
  data: ElasticRoundAnalyticsRound;
  courseInfo: ElasticCourseData;
  totalStrokes: number;
  totalScore: number;
  format: TournamentEventTypeEnum;
}> = memo(({
  data,
  courseInfo,
  totalStrokes,
  totalScore,
  format
}) => {
  const isNA = totalScore >= 10000;
  const isSubstitute = data.playerStatus === StrokeDraftDataPlayerStatusEnum.Substitute;

  // TODO: Refactor the below into an extracted function

  const cumulativeStrokes = useMemo(() => {
    // Adjust strokes for starting hole
    const adjStrokes = normaliseHoles(data.strokes ?? [], data.startingHole);
    const startingHole = data.startingHole ?? 1;
    const numHoles = courseInfo?.pars.length ?? 0;
    const cum: (number | null)[] = [];
    for (let i = 0; i < numHoles; i++) {
      const holeId = (startingHole + i - 1) % numHoles + 1;
      const slice = adjStrokes.slice(0, i + 1);
      cum[holeId - 1] = slice.every(x => typeof x === 'number') ? sum(slice) : null;
    }
    return cum;
  }, [data]);
  const cumulativePar = useMemo(() => {
    // Adjust pars for starting hole
    const adjPars = normaliseHoles(courseInfo?.pars ?? [], data.startingHole);
    const startingHole = data.startingHole ?? 1;
    const numHoles = courseInfo?.pars.length ?? 0;
    const cum: number[] = [];
    for (let i = 0; i < numHoles; i++) {
      const holeId = (startingHole + i - 1) % numHoles + 1;
      cum[holeId - 1] = sum(adjPars.slice(0, i + 1));
    }
    return cum;
  }, [courseInfo]);
  const outerClasses = 'lg:rounded-lg overflow-clip border border-gray-200';
  const headerClasses = 'px-4 py-2 lg:py-3 bg-gray-50 flex items-center';
  if (isSubstitute) {
    return <div className={outerClasses}>
        <header className={headerClasses}>
          <h3 className="block text-sm lg:text-lg">Round {data.roundId}: SUB</h3>
        </header>
      </div>;
  }
  return data && courseInfo ? <div className={outerClasses}>
      <header className={headerClasses}>
        <h3 className="block text-sm lg:text-lg">
          Round {data.roundId}: {courseInfo.courseName}
        </h3>
      </header>
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <Cell cellType="header">Hole</Cell>
              {range(1, 10).map(holeId => <Cell cellType="header" key={holeId}>
                  {holeId}
                </Cell>)}
              <Cell cellType="header" variant="total">
                OUT
              </Cell>
              {range(10, 19).map(holeId => <Cell cellType="header" key={holeId}>
                  {holeId}
                </Cell>)}
              <Cell cellType="header" variant="total">
                IN
              </Cell>
              <Cell cellType="header" variant="total">
                RD
              </Cell>
            </tr>
            <RoundScorecardRow label="Yards" accumulator="sum" data={courseInfo.yards} total={sum(courseInfo.yards)} startingHole={data.startingHole} />
            <RoundScorecardRow label="Par" accumulator="sum" data={courseInfo.pars} total={sum(courseInfo.pars)} startingHole={data.startingHole} />
            <RoundScorecardRow label="Strokes" accumulator="sum" data={data.strokes ?? []} total={totalStrokes} cellRenderer={(value, i) => {
            if (isNA) {
              return 'X';
            }
            return typeof value === 'number' ? value === 0 ? 'X' : displayHoleResult({
              strokes: value,
              par: courseInfo.pars[i]
            }, 'stroke', format !== 'Stroke Play') : '-';
          }} totalCellRenderer={(value, start, end) => {
            if (isNA) {
              return 'N/A';
            }
            const strokeCount = data.strokes?.filter(x => typeof x === 'number').length ?? 0;
            if (!end && strokeCount < 18) {
              return '-';
            }
            if (end && strokeCount < end) {
              return '-';
            }
            return typeof value === 'number' ? displayScoreResult({
              strokes: value,
              par: typeof start === 'number' ? sum(courseInfo.pars.slice(start, end)) : sum(courseInfo.pars)
            }, 'stroke') : '-';
          }} startingHole={data.startingHole} />
            <RoundScorecardRow label="To Par" data={cumulativeStrokes ?? []} total={totalStrokes} accumulator="last" cellRenderer={(value, i) => {
            if (isNA) {
              return '-';
            }
            return typeof value === 'number' && typeof data.strokes?.[i] === 'number' && value > 0 ? displayScoreResult({
              strokes: value,
              par: cumulativePar[i]
            }, 'score') : '-';
          }} totalCellRenderer={(value, start, end) => {
            if (isNA) {
              return 'N/A';
            }
            const strokeCount = data.strokes?.filter(x => typeof x === 'number').length ?? 0;
            if (!end && strokeCount < 18) {
              return '-';
            }
            if (end && strokeCount < end) {
              return '-';
            }
            return typeof value === 'number' && value > 0 ? displayScoreResult({
              strokes: sum(data.strokes?.slice(start, end)),
              par: typeof start === 'number' ? sum(courseInfo.pars.slice(start, end)) : sum(courseInfo.pars)
            }, 'score') : '-';
          }} startingHole={data.startingHole} />
          </thead>
        </table>
      </div>
    </div> : null;
});